import { useState, useEffect, useRef } from 'react'
import { Printer, Brain, RotateCcw, EyeOff } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import api from '../../api/axios'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}` } catch { return s }
}

const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']

function getPeriodos() {
  const hoje = new Date()
  const ano = hoje.getFullYear()
  const mes = hoje.getMonth()
  const fmt = d => d.toISOString().split('T')[0]
  const ini = (y, m) => fmt(new Date(y, m, 1))
  const fim = (y, m) => fmt(new Date(y, m + 1, 0))
  return [
    { label: 'Último mês',      de: ini(ano, mes - 1),  ate: fim(ano, mes - 1) },
    { label: 'Últimos 3 meses', de: ini(ano, mes - 3),  ate: fmt(hoje) },
    { label: 'Últimos 6 meses', de: ini(ano, mes - 6),  ate: fmt(hoje) },
    { label: 'Último ano',      de: ini(ano - 1, mes),  ate: fmt(hoje) },
    { label: 'Ano atual',       de: ini(ano, 0),         ate: fmt(hoje) },
    { label: 'Ano anterior',    de: ini(ano - 1, 0),     ate: fim(ano - 1, 11) },
    { label: 'Personalizado',   de: '',                  ate: '' },
  ]
}

const S = {
  page:   { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr:    { borderBottom: '2px solid #1f2937', paddingBottom: 12, marginBottom: 18 },
  hdrT1:  { fontSize: 16, fontWeight: 700 },
  hdrT2:  { fontSize: 11, color: '#374151', marginTop: 3 },
  hdrT3:  { fontSize: 11, color: '#6b7280', marginTop: 2 },
  tbl:    { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th:     { borderBottom: '2px solid #1f2937', padding: '8px', textAlign: 'left',   fontWeight: 700, fontSize: 11, background: '#1f2937', color: '#fff' },
  thC:    { borderBottom: '2px solid #1f2937', padding: '8px', textAlign: 'center', fontWeight: 700, fontSize: 11, background: '#1f2937', color: '#fff' },
  thR:    { borderBottom: '2px solid #1f2937', padding: '8px', textAlign: 'right',  fontWeight: 700, fontSize: 11, background: '#1f2937', color: '#fff' },
  td:     { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC:    { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR:    { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'right',  verticalAlign: 'middle', fontSize: 12 },
  totRow: { fontWeight: 700, borderTop: '2px solid #1f2937', background: '#f3f4f6' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

const PAPEL_LABELS = { comprador: 'Compradores', vendedor: 'Vendedores', ambos: 'Compradores e Vendedores' }

export default function InteligenciaPecuaria() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const periodos = getPeriodos()
  const [periodoIdx, setPeriodoIdx]     = useState(4)
  const [dataInicio, setDataInicio]     = useState(periodos[4].de)
  const [dataFim, setDataFim]           = useState(periodos[4].ate)

  const [papel, setPapel]               = useState('comprador')
  const [racaId, setRacaId]             = useState('')
  const [tipoAnimalId, setTipoAnimalId] = useState('')
  const [idadeId, setIdadeId]           = useState('')
  const [sexo, setSexo]                 = useState('')
  const [estado, setEstado]             = useState('')
  const [cidade, setCidade]             = useState('')
  const [orderBy, setOrderBy]           = useState('valor')
  const [ocultar, setOcultar]           = useState(false)

  const [racas, setRacas]   = useState([])
  const [tipos, setTipos]   = useState([])
  const [idades, setIdades] = useState([])

  const [dados, setDados]           = useState(null)
  const [loading, setLoading]       = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl]         = useState(null)
  const [showPdf, setShowPdf]       = useState(false)
  const iframeRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    Promise.all([
      api.get(`/organizadores/${orgId}/racas`),
      api.get(`/organizadores/${orgId}/tipos-animais`),
      api.get(`/organizadores/${orgId}/idades`),
    ]).then(([r, t, i]) => {
      setRacas(r.data || [])
      setTipos(t.data || [])
      setIdades(i.data || [])
    }).catch(() => {})
  }, [orgId])

  const buildQs = (extra = {}) => {
    const qs = new URLSearchParams()
    qs.set('papel', extra.papel ?? papel)
    const ri = extra.racaId !== undefined ? extra.racaId : racaId
    const ti = extra.tipoAnimalId !== undefined ? extra.tipoAnimalId : tipoAnimalId
    const ii = extra.idadeId !== undefined ? extra.idadeId : idadeId
    const sx = extra.sexo !== undefined ? extra.sexo : sexo
    const es = extra.estado !== undefined ? extra.estado : estado
    const ci = extra.cidade !== undefined ? extra.cidade : cidade
    const di = extra.dataInicio !== undefined ? extra.dataInicio : dataInicio
    const df = extra.dataFim !== undefined ? extra.dataFim : dataFim
    const ob = extra.orderBy !== undefined ? extra.orderBy : orderBy
    if (ri)  qs.set('raca_id', ri)
    if (ti)  qs.set('tipo_animal_id', ti)
    if (ii)  qs.set('idade_id', ii)
    if (sx)  qs.set('sexo', sx)
    if (es)  qs.set('estado', es)
    if (ci)  qs.set('cidade', ci)
    if (di)  qs.set('data_inicio', di)
    if (df)  qs.set('data_fim', df)
    qs.set('order_by', ob)
    return qs.toString()
  }

  const fetchDados = (qs) => {
    if (!orgId) return
    setLoading(true)
    api.get(`/organizadores/${orgId}/inteligencia-pecuaria?${qs}`)
      .then(r => setDados(r.data))
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }

  const scheduleSearch = (delay = 350, extra = {}) => {
    clearTimeout(debounceRef.current)
    const qs = buildQs(extra)
    debounceRef.current = setTimeout(() => fetchDados(qs), delay)
  }

  // Auto-busca ao mudar qualquer filtro select
  const handlePapel = v => { setPapel(v); scheduleSearch(200, { papel: v }) }
  const handleRaca = v => { setRacaId(v); scheduleSearch(200, { racaId: v }) }
  const handleTipo = v => { setTipoAnimalId(v); scheduleSearch(200, { tipoAnimalId: v }) }
  const handleIdade = v => { setIdadeId(v); scheduleSearch(200, { idadeId: v }) }
  const handleSexo = v => { setSexo(v); scheduleSearch(200, { sexo: v }) }
  const handleEstado = v => { setEstado(v); scheduleSearch(200, { estado: v }) }
  const handleOrderBy = v => { setOrderBy(v); scheduleSearch(200, { orderBy: v }) }

  // Cidade com debounce maior (digitação)
  const handleCidade = v => {
    setCidade(v)
    clearTimeout(debounceRef.current)
    const qs = buildQs({ cidade: v })
    debounceRef.current = setTimeout(() => fetchDados(qs), 700)
  }

  const handlePeriodoChange = idx => {
    setPeriodoIdx(idx)
    if (idx < periodos.length - 1) {
      const di = periodos[idx].de
      const df = periodos[idx].ate
      setDataInicio(di)
      setDataFim(df)
      scheduleSearch(200, { dataInicio: di, dataFim: df })
    }
  }

  const handleDataInicio = v => {
    setDataInicio(v)
    scheduleSearch(400, { dataInicio: v })
  }

  const handleDataFim = v => {
    setDataFim(v)
    scheduleSearch(400, { dataFim: v })
  }

  // Busca manual (botão Pesquisar)
  const handlePesquisar = () => {
    clearTimeout(debounceRef.current)
    fetchDados(buildQs())
  }

  const handleLimpar = () => {
    clearTimeout(debounceRef.current)
    setPapel('comprador'); setRacaId(''); setTipoAnimalId(''); setIdadeId('')
    setSexo(''); setEstado(''); setCidade(''); setOrderBy('valor')
    setDados(null)
  }

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = buildQs()
      const resp = await api.get(
        `/organizadores/${orgId}/inteligencia-pecuaria/pdf?${qs}&ocultar_valores=${ocultar}`,
        { responseType: 'blob', timeout: 60000 }
      )
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.message || 'tente novamente'))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const clientes   = dados?.clientes || []
  const totalLotes = clientes.reduce((s, c) => s + c.lotes, 0)
  const totalCab   = clientes.reduce((s, c) => s + c.qtd_animais, 0)
  const totalValor = clientes.reduce((s, c) => s + c.valor, 0)

  const filtrosAtivos = [
    racaId       && `Raça: ${racas.find(r => r.id == racaId)?.nome || racaId}`,
    tipoAnimalId && `Tipo: ${tipos.find(t => t.id == tipoAnimalId)?.nome || tipoAnimalId}`,
    idadeId      && `Idade: ${idades.find(i => i.id == idadeId)?.nome || idadeId}`,
    sexo         && `Sexo: ${sexo}`,
    estado       && `Estado: ${estado}`,
    cidade       && `Cidade: ${cidade}`,
  ].filter(Boolean)

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center gap-2">
        <Brain className="w-5 h-5 text-purple-600" />
        <h1 className="text-lg font-semibold text-gray-800">Inteligência Pecuária</h1>
        <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">Premium</span>
      </div>

      {/* Card único de filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ── Seção Período ── */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-14 shrink-0">Período</span>

            <div className="flex flex-wrap gap-1.5">
              {periodos.map((p, i) => (
                <button key={i} type="button" onClick={() => handlePeriodoChange(i)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap border ${
                    periodoIdx === i
                      ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>

            {periodoIdx === periodos.length - 1 && (
              <div className="flex items-center gap-2 ml-2">
                <input type="date" value={dataInicio} onChange={e => handleDataInicio(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                <span className="text-gray-400 text-sm">→</span>
                <input type="date" value={dataFim} onChange={e => handleDataFim(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              </div>
            )}
          </div>
        </div>

        {/* ── Seção Filtros de Perfil ── */}
        <div className="px-5 py-4">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Filtros de Perfil</span>

          <div className="flex flex-wrap gap-3 mt-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Papel</label>
              <select value={papel} onChange={e => handlePapel(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[160px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="comprador">Compradores</option>
                <option value="vendedor">Vendedores</option>
                <option value="ambos">Compradores e Vendedores</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Raça</label>
              <select value={racaId} onChange={e => handleRaca(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="">Todas</option>
                {racas.filter(r => r.ativo !== false).map(r => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Tipo de Animal</label>
              <select value={tipoAnimalId} onChange={e => handleTipo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[150px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="">Todos</option>
                {tipos.filter(t => t.ativo !== false).map(t => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Idade</label>
              <select value={idadeId} onChange={e => handleIdade(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[120px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="">Todas</option>
                {idades.filter(i => i.ativo !== false).map(i => (
                  <option key={i.id} value={i.id}>{i.nome}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Sexo</label>
              <select value={sexo} onChange={e => handleSexo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[110px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="">Todos</option>
                <option value="Macho">Macho</option>
                <option value="Fêmea">Fêmea</option>
                <option value="Misto">Misto</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Estado (UF)</label>
              <select value={estado} onChange={e => handleEstado(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[90px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="">Todos</option>
                {ESTADOS_BR.map(uf => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Cidade</label>
              <input type="text" value={cidade} onChange={e => handleCidade(e.target.value)}
                placeholder="Buscar cidade..."
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[150px] focus:outline-none focus:ring-2 focus:ring-purple-400" />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Ordenar por</label>
              <select value={orderBy} onChange={e => handleOrderBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[130px] focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="valor">Valor Total</option>
                <option value="cabecas">Cabeças</option>
                <option value="lotes">Lotes</option>
              </select>
            </div>
          </div>

          {/* Tags de filtros ativos */}
          {filtrosAtivos.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {filtrosAtivos.map((f, i) => (
                <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">{f}</span>
              ))}
            </div>
          )}
        </div>

        {/* ── Rodapé de ações ── */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
          <button onClick={handleLimpar}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <RotateCcw size={13} /> Limpar filtros
          </button>

          <div className="flex items-center gap-3">
            {loading && (
              <span className="flex items-center gap-1.5 text-xs text-purple-600 font-medium">
                <span className="w-3 h-3 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                Buscando...
              </span>
            )}

            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={ocultar} onChange={e => setOcultar(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-purple-600" />
              <EyeOff size={13} className="text-gray-400" />
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Ocultar valores</span>
            </label>

            <div className="h-4 w-px bg-gray-300" />

            <button onClick={handlePesquisar} disabled={loading}
              className="px-4 py-1.5 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors">
              Pesquisar
            </button>

            <button onClick={handleImprimir} disabled={!dados || loadingPdf || clientes.length === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={14} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Brain className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Selecione os filtros e clique em <strong>Pesquisar</strong></p>
          <p className="text-gray-300 text-xs mt-1">Descubra quem compra ou vende por raça, tipo, idade e região</p>
        </div>
      ) : clientes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhum cliente encontrado com os filtros selecionados.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Resumo */}
          <div className="flex items-center gap-6 px-5 py-3 bg-purple-50 border-b border-purple-100">
            <span className="text-sm text-purple-800 font-semibold">{clientes.length} clientes encontrados</span>
            <span className="text-sm text-purple-700">{totalCab.toLocaleString('pt-BR')} cabeças</span>
            <span className="text-sm text-purple-700">{totalLotes} lotes</span>
            {!ocultar && <span className="text-sm text-purple-700 font-semibold">{brl(totalValor)}</span>}
          </div>

          {/* Tabela */}
          <div style={S.page}>
            <div style={{ ...S.hdr, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ minWidth: 140 }}>
                {organizadorAtivo?.logo_url && (
                  <img src={organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`}
                    alt="logo" style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain' }} />
                )}
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 16px' }}>
                <div style={S.hdrT1}>INTELIGÊNCIA PECUÁRIA — {PAPEL_LABELS[papel].toUpperCase()}</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                {(dataInicio || dataFim) && (
                  <div style={S.hdrT3}>Período: {fmtDate(dataInicio)} a {fmtDate(dataFim)}</div>
                )}
                {filtrosAtivos.length > 0 && (
                  <div style={{ ...S.hdrT3, fontSize: 10 }}>{filtrosAtivos.join('  |  ')}</div>
                )}
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain' }} />
              </div>
            </div>

            <table style={S.tbl}>
              <thead>
                <tr>
                  <th style={{ ...S.thC, width: '4%' }}>Pos.</th>
                  <th style={{ ...S.thC, width: '3%' }}>ID</th>
                  <th style={{ ...S.th,  width: '22%' }}>Nome</th>
                  <th style={{ ...S.th,  width: '14%' }}>Fazenda</th>
                  <th style={{ ...S.th,  width: '13%' }}>Cidade</th>
                  <th style={{ ...S.thC, width: '4%' }}>UF</th>
                  <th style={{ ...S.th,  width: '11%' }}>Telefone</th>
                  <th style={{ ...S.thC, width: '5%' }}>Lotes</th>
                  <th style={{ ...S.thC, width: '7%' }}>Cabeças</th>
                  {!ocultar && <th style={{ ...S.thR, width: '10%' }}>Valor Total</th>}
                  <th style={{ ...S.th,  width: '7%' }}>Últ. Leilão</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={S.tdC}>{String(i + 1).padStart(3, '0')}</td>
                    <td style={S.tdC}>{c.id}</td>
                    <td style={S.td}><strong>{c.nome}</strong></td>
                    <td style={S.td}>{c.fazenda || '—'}</td>
                    <td style={S.td}>{c.cidade || '—'}</td>
                    <td style={S.tdC}>{c.estado || '—'}</td>
                    <td style={S.td}>{c.telefone || '—'}</td>
                    <td style={S.tdC}>{c.lotes}</td>
                    <td style={S.tdC}>{c.qtd_animais}</td>
                    {!ocultar && <td style={S.tdR}>{brl(c.valor)}</td>}
                    <td style={S.td}>{fmtDate(c.ultimo_leilao) || '—'}</td>
                  </tr>
                ))}
                <tr style={S.totRow}>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }} colSpan={2} />
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}><strong>TOTAL: {clientes.length} clientes</strong></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }} colSpan={4} />
                  <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalLotes}</strong></td>
                  <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalCab.toLocaleString('pt-BR')}</strong></td>
                  {!ocultar && <td style={{ ...S.tdR, borderTop: '2px solid #1f2937' }}><strong>{brl(totalValor)}</strong></td>}
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }} />
                </tr>
              </tbody>
            </table>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Inteligência Pecuária — {PAPEL_LABELS[papel]}</span>
              <div className="flex items-center gap-2">
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir
                </button>
                <button onClick={closePdf}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Inteligência Pecuária"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
